/**
 * Google Maps runtime-health reporting.
 *
 * The app loads Google Maps through a single seam (MapApiProvider), but until
 * now a runtime failure there was invisible: an invalid/over-quota/billing-
 * disabled key renders the greyed "For development purposes only" watermark and
 * throws nothing, so no log, metric, or alert ever fired. This module turns the
 * three detectable failure modes into a typed signal on the existing browser
 * telemetry pipeline (clientLogger → Loki) plus a Prometheus-bound counter
 * (web_maps_degraded).
 *
 * Detection is inherently limited by what Google exposes:
 *  - `auth_failure`      — `window.gm_authFailure` (invalid key / referrer / some
 *                          billing failures). A real callback.
 *  - `script_load_error` — `<APIProvider onError>` (the SDK script failed to load).
 *  - `dev_watermark`     — DOM sniffing. Google gives NO callback for the
 *                          quota/billing "for development purposes only" overlay,
 *                          so a MutationObserver watching for that literal text is
 *                          the only signal available. The brittleness is confined
 *                          to this file on purpose.
 */

import { clientLogger } from '@/shared/lib/client-logger';
import {
  reportWebMapsDegraded,
  type WebMapsDegradedReason,
} from '@/shared/lib/metrics/client-metrics_gen';
import { assertNever } from '@letsgethome/lib/utils/enums';

declare global {
  interface Window {
    /** Google Maps invokes this global on an auth/billing failure, if present. */
    gm_authFailure?: () => void;
  }
}

/**
 * The detectable Maps failure modes. NOT a hand-written union — an alias of the
 * generated `web_maps_degraded.reason` vocabulary, so this module can never emit
 * (or `messageFor`-handle) a reason the metric does not declare: a reason added
 * to `web/manifests/metrics.yaml` widens this type and the exhaustive `switch`
 * below then fails to compile until it is handled. See metrics.yaml.
 */
export type MapsFailureReason = WebMapsDegradedReason;

const WATERMARK_TEXT = 'For development purposes only';
/** Stop DOM-watching this long after a map mounts — the watermark, if any, is up by then. */
const WATERMARK_OBSERVE_MS = 30_000;

// Emit each reason at most once per page load: gm_authFailure can fire repeatedly
// and the MutationObserver can see many mutations, but the signal is "this session
// saw a degraded map", not a per-event tally.
const reported = new Set<MapsFailureReason>();

function messageFor(reason: MapsFailureReason): string {
  switch (reason) {
    case 'auth_failure':
      return 'Google Maps auth failure (gm_authFailure): invalid key, referrer restriction, or billing disabled';
    case 'script_load_error':
      return 'Google Maps JS API script failed to load';
    case 'dev_watermark':
      return 'Google Maps rendered the "for development purposes only" watermark (quota/billing degraded)';
    default:
      return assertNever(reason);
  }
}

/**
 * Report a Maps failure to Loki (as a `component: 'GoogleMaps'` error log) and to
 * the `web_maps_degraded` counter. Deduped per reason per page load.
 */
export function reportMapsFailure(
  reason: MapsFailureReason,
  context: Record<string, unknown> = {}
): void {
  if (typeof window === 'undefined') return;
  if (reported.has(reason)) return;
  reported.add(reason);

  clientLogger.error({ component: 'GoogleMaps', reason, ...context }, messageFor(reason));
  reportWebMapsDegraded(1, { reason });
}

/**
 * Install the `window.gm_authFailure` handler. Call this ONCE, as early as
 * possible (before any Maps script loads), so Google's callback has somewhere to
 * land — see entry-client.tsx.
 */
export function installMapsAuthFailureHandler(): void {
  if (typeof window === 'undefined') return;
  window.gm_authFailure = () => reportMapsFailure('auth_failure');
}

// One observer at a time — MapApiProvider can mount for several maps on a page.
let watching = false;

/**
 * Watch for the "for development purposes only" watermark and report it. Returns
 * a disconnect function. No-op (returns a noop) on the server, without
 * MutationObserver, or if already watching. Self-disconnects on first detection
 * or after {@link WATERMARK_OBSERVE_MS}.
 */
export function observeMapsWatermark(root: ParentNode = document.body): () => void {
  const noop = (): void => {};
  if (typeof window === 'undefined' || typeof MutationObserver === 'undefined') return noop;
  if (watching) return noop;

  // Already degraded before we started watching?
  if (root.textContent?.includes(WATERMARK_TEXT)) {
    reportMapsFailure('dev_watermark');
    return noop;
  }

  watching = true;
  const observer = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      for (const node of mutation.addedNodes) {
        if (node.textContent?.includes(WATERMARK_TEXT)) {
          reportMapsFailure('dev_watermark');
          stop();
          return;
        }
      }
    }
  });
  observer.observe(root, { childList: true, subtree: true });
  const timer = window.setTimeout(stop, WATERMARK_OBSERVE_MS);

  function stop(): void {
    if (!watching) return;
    watching = false;
    observer.disconnect();
    window.clearTimeout(timer);
  }

  return stop;
}

/** Test-only: reset the per-page dedup + observer guard between cases. */
export function resetMapsHealthForTest(): void {
  reported.clear();
  watching = false;
}
